'use client'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAuth } from '@/providers/Auth'
import { LogOutIcon, MenuIcon, BookOpen, User, ExternalLink } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import type { ResolvedLink } from '@/utilities/resolveNavigationLink'
import { userHasScannerAccess } from '@/utilities/scannerAccess'

type MobileMenuProps = {
  headerLinks: ResolvedLink[]
}

export function MobileMenu({ headerLinks }: MobileMenuProps) {
  const { user } = useAuth()
  const canUseScanner = userHasScannerAccess(user)

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  useEffect(() => {
    setIsOpen(false)
  }, [pathname, searchParams])

  return (
    <Sheet onOpenChange={setIsOpen} open={isOpen}>
      <SheetTrigger
        aria-label="Menu openen"
        className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-border/60 bg-background text-foreground transition-all hover:bg-muted/50 hover:border-primary/30"
      >
        <MenuIcon aria-hidden="true" className="h-5 w-5" />
      </SheetTrigger>

      <SheetContent side="right" className="w-80 px-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/50">
          <SheetTitle className="text-xl font-bold">Menu</SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Navigeer door je account
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 py-6 space-y-4">
          {/* Navigation Links */}
          <div className="rounded-2xl border border-border/50 bg-card/50 p-4">
            <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Menu
            </p>
            <nav className="space-y-1">
              <Link
                href="/cursussen"
                className={clsx(
                  'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                  pathname === '/cursussen' || pathname?.startsWith('/cursussen/')
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                )}
              >
                Cursussen
              </Link>
              <Link
                href="/blog"
                className={clsx(
                  'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                  pathname === '/blog' || pathname?.startsWith('/blog/')
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                )}
              >
                Blogs
              </Link>
              <Link
                href="/vezelkaarten"
                className={clsx(
                  'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                  pathname === '/vezelkaarten'
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                )}
              >
                Vezelkaarten
              </Link>
              {canUseScanner && (
                <Link
                  href="/glutenvrij-scanner"
                  className={clsx(
                    'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                    pathname === '/glutenvrij-scanner'
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )}
                >
                  Scanner
                </Link>
              )}
              <Link
                href="/contact"
                className={clsx(
                  'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                  pathname === '/contact'
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                )}
              >
                Contact
              </Link>
              {headerLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  target={link.newTab ? '_blank' : undefined}
                  rel={link.newTab ? 'noopener noreferrer' : undefined}
                  className={clsx(
                    'flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                    pathname === link.href
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  )}
                >
                  {link.label}
                  {link.newTab && <ExternalLink className="h-3 w-3" />}
                </Link>
              ))}
            </nav>
          </div>

          {user ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-border/50 bg-card/50 p-4">
                <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Mijn account
                </p>
                <nav className="space-y-1">
                  <Link
                    href="/account"
                    className={clsx(
                      'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                      pathname === '/account'
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                    )}
                  >
                    <User className="h-4 w-4" />
                    Mijn account
                  </Link>
                  <Link
                    href="/orders"
                    className={clsx(
                      'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                      pathname === '/orders' || pathname.includes('/orders')
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                    )}
                  >
                    <BookOpen className="h-4 w-4" />
                    Mijn cursussen
                  </Link>
                </nav>
              </div>

              <div className="rounded-2xl border border-border/50 bg-card/50 p-4">
                <Link
                  href="/logout"
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-all hover:bg-destructive/5 hover:text-destructive"
                >
                  <LogOutIcon className="h-4 w-4" />
                  Uitloggen
                </Link>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-border/50 bg-card/50 p-4">
              <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Account
              </p>
              <p className="mb-4 px-3 text-sm text-muted-foreground">
                Log in of maak een account aan om toegang te krijgen tot je cursussen.
              </p>
              <div className="flex flex-col gap-3 px-3">
                <Button asChild variant="outline" className="w-full">
                  <Link href="/inloggen">Inloggen</Link>
                </Button>
                <Button asChild className="w-full">
                  <Link href="/registreren">Account aanmaken</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
